import { toolkitUrls } from '@/util/links'

// Keys, category labels and category profile ids are a cross-SPA contract: both SPAs read one
// stored selection, so these must match toolkit-spa's catalog exactly. Both specs assert that.
export const CUSTOMIZATIONS = {
  GOALS: 'goals',
  AUTO_REVIEW: 'autoReview'
} as const

export type CustomizationKey = (typeof CUSTOMIZATIONS)[keyof typeof CUSTOMIZATIONS]

export interface DemoCategory {
  label: string
  profileId: string
}

export interface DemoLink {
  label: string
  url: string
}

export interface CustomizationCatalogEntry {
  key: CustomizationKey
  title: string
  description: string
  // Where to send a presenter who is not on a page that runs this customization, one per page.
  demoLinks: DemoLink[]
  pagePatterns: RegExp[]
  categories: DemoCategory[]
}

// Paths are router paths, without the /toolkit/v base. Vue 2 ran both demos on the anchor page and
// its saved and shared copies.
export const ANCHOR_PAGES: RegExp[] = [/^\/anchor-page\//, /^\/library\/anchor-page\//, /^\/share\/anchor-page\//]

// The other places auto-review runs: the Present Levels wizard and its saved summaries, and Impact
// of Disability statements, generated and saved. The shared present levels summary is not here: it
// renders no review button.
export const REVIEW_PAGES: RegExp[] = [...ANCHOR_PAGES, /^\/presentlevels\/v2/, /^\/impact-of-disability/]

// The goal customizations picker is live on ANCHOR_PAGES, and the anchor page store reads its
// selection. The auto-review picker is live on REVIEW_PAGES, and the content review store's
// `reviewProfileId` reads its selection.
export const CUSTOMIZATION_CATALOG: CustomizationCatalogEntry[] = [
  {
    key: CUSTOMIZATIONS.GOALS,
    title: 'Goal Customizations',
    description: 'Apply a pre-configured customization to goals and anchor pages.',
    demoLinks: [{ label: 'Browse goals to run this demo', url: toolkitUrls.browse }],
    pagePatterns: ANCHOR_PAGES,
    categories: [
      { label: 'Week-based goal', profileId: 'hawaii' },
      { label: 'Baseline reference', profileId: 'minnesota' },
      { label: 'Measurement criteria', profileId: 'california-irvine' },
      { label: 'State compliance format', profileId: 'tennessee' },
      { label: 'Separate annual and baseline', profileId: 'engineering-testing' }
    ]
  },
  {
    key: CUSTOMIZATIONS.AUTO_REVIEW,
    title: 'Auto-Review Rubric',
    description:
      "Review goals, present levels and impact statements against a district's rubric instead of Goalbook's criteria.",
    demoLinks: [
      // The v2 session directly: auto-review only exists in the v2 wizard, and the /presentlevels
      // entry lands on the legacy wizard.
      { label: 'Open the Present Levels Wizard to run this demo', url: toolkitUrls.presentlevelsWizardV2 },
      {
        label: 'Compose an Impact of Disability statement to run this demo',
        url: toolkitUrls.impactOfDisability(true)
      }
    ],
    pagePatterns: REVIEW_PAGES,
    categories: [
      { label: 'Full IEP checklist', profileId: 'georgia_dekalb' },
      { label: 'PLAAFP quality rubric', profileId: 'california-san_bernardino-draft' },
      { label: 'Regulation-cited rubric', profileId: 'maryland-stmarys' },
      { label: 'Impact and goals rubric', profileId: 'missouri_ssd' }
    ]
  }
]

export const isCustomizationOnPage = (
  customization: CustomizationCatalogEntry,
  path: string
): boolean => customization.pagePatterns.some((pattern) => pattern.test(path))
